<template>
  <v-menu
    v-model="open"
    :close-on-content-click="false"
    location="bottom end"
    offset="8"
    max-width="380"
  >
    <template #activator="{ props: menuProps }">
      <v-badge
        :model-value="unreadCount > 0"
        :content="unreadCount > 99 ? '99+' : unreadCount"
        color="error"
        offset-x="6"
        offset-y="6"
      >
        <v-btn
          v-bind="menuProps"
          icon="mdi-bell-outline"
          variant="text"
          :aria-label="`Értesítések${
            unreadCount > 0 ? `, ${unreadCount} olvasatlan` : ''
          }`"
        ></v-btn>
      </v-badge>
    </template>

    <v-card width="380" class="cv-notification-card">
      <div class="d-flex align-center pa-3">
        <span class="text-subtitle-2 font-weight-medium">Értesítések</span>
        <v-spacer></v-spacer>
        <v-btn
          v-if="unreadCount > 0"
          variant="text"
          size="small"
          density="comfortable"
          @click="markAllAsRead"
        >
          Mind olvasottnak jelöl
        </v-btn>
      </div>
      <v-divider></v-divider>

      <div v-if="loading" class="pa-3">
        <v-skeleton-loader type="list-item-two-line"></v-skeleton-loader>
        <v-skeleton-loader type="list-item-two-line"></v-skeleton-loader>
      </div>

      <div v-else-if="notifications.length === 0" class="text-center pa-8">
        <v-icon size="48" color="grey-lighten-1">mdi-bell-outline</v-icon>
        <div class="text-body-2 font-weight-medium mt-2">Nincs értesítés</div>
        <div class="text-caption text-medium-emphasis">
          Itt jelennek meg az igényeidhez tartozó események.
        </div>
      </div>

      <v-list v-else class="cv-notification-list py-0" density="comfortable">
        <v-list-item
          v-for="notification in notifications"
          :key="notification.id"
          :class="{ 'cv-notification-unread': !notification.readAt }"
          @click="openNotification(notification)"
        >
          <template #prepend>
            <v-icon
              :icon="notificationTypeIcon(notification.type)"
              :color="notification.readAt ? 'grey' : 'primary'"
            ></v-icon>
          </template>

          <v-list-item-title class="text-body-2 font-weight-medium">
            {{ notification.title }}
          </v-list-item-title>
          <v-list-item-subtitle v-if="notification.body" class="text-caption">
            {{ notification.body }}
          </v-list-item-subtitle>
          <div class="text-caption text-medium-emphasis mt-1">
            {{ relativeTime(notification.createdAt) }}
          </div>

          <template #append>
            <span v-if="!notification.readAt" class="cv-unread-dot"></span>
          </template>
        </v-list-item>

        <div v-if="hasMore" class="pa-2 text-center">
          <v-btn
            variant="text"
            size="small"
            density="comfortable"
            :loading="loadingMore"
            @click="loadMore"
          >
            Korábbiak betöltése
          </v-btn>
        </div>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { formatDistanceToNow } from "date-fns";
import { hu } from "date-fns/locale";
import { useAuthStore } from "@/store/AuthStore";
import { useNotifications } from "@/services/useNotifications";
import type { NotificationDTO } from "@/services/NotificationService";
import { notificationTypeIcon } from "@/utils/notificationLabels";
import { notificationLink } from "@/utils/notificationLink";

const router = useRouter();
const authStore = useAuthStore();
const {
  notifications,
  unreadCount,
  loading,
  loadingMore,
  hasMore,
  connected,
  fetchAll,
  loadMore,
  markAsRead,
  markAllAsRead,
} = useNotifications();

const open = ref(false);

// The stream normally delivers the list on connect. Fetching here covers the case where
// it never opened, without polling for it.
watch(open, (isOpen) => {
  if (isOpen && !connected.value) {
    fetchAll();
  }
});

const relativeTime = (timestamp: string): string =>
  formatDistanceToNow(new Date(timestamp), { addSuffix: true, locale: hu });

async function openNotification(notification: NotificationDTO) {
  await markAsRead(notification.id);
  const target = notificationLink(notification, authStore.isAdmin);
  if (target) {
    open.value = false;
    router.push(target);
  }
}
</script>

<style scoped>
.cv-notification-list {
  max-height: 60vh;
  overflow-y: auto;
}

.cv-notification-unread {
  background: var(--cv-brand-tint);
}

.cv-unread-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--cv-brand);
}
</style>
